// Mapeos de dominio -> tono visual, compartidos por los componentes *Badge. Mantiene
// `StatusBadge` agnóstico del significado de cada estado — cada dominio (mini app user,
// paper trade liquidado) aporta su propio mapeo aquí, en un solo sitio.

export type BadgeTone = 'neutral' | 'success' | 'danger' | 'warn' | 'accent'

export const MINI_APP_STATUS_TONE: Record<string, BadgeTone> = {
  pending: 'warn',
  active: 'success',
  suspended: 'neutral',
  expired: 'neutral',
  blocked: 'danger',
}

export const MINI_APP_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendiente',
  active: 'Activo',
  suspended: 'Suspendido',
  expired: 'Vencido',
  blocked: 'Bloqueado',
}

export const SETTLED_STATUS_TONE: Record<string, BadgeTone> = {
  pendiente: 'neutral',
  ganada: 'success',
  perdida: 'danger',
  void: 'neutral',
}

export const SETTLED_STATUS_LABEL: Record<string, string> = {
  pendiente: 'Pendiente',
  ganada: 'Acertó',
  perdida: 'Falló',
  void: 'Anulado',
}

// P0.2 (Sports Identity & Visual Truth): `parlay.status` es un campo ALMACENADO
// ("valido"/"ganada"/"perdida"/"void"/"no_formado") que solo cambia cuando el motor de
// liquidación real corre -- nunca se recalcula por el simple paso del tiempo. El backend
// ya expone `is_bettable` recalculado en VIVO en cada lectura (ver `_parlay_to_dict`,
// `is_fixture_bettable()`) -- antes de este fix, `SameGameParlayCard.tsx`/`ParlayCard.tsx`
// pasaban `parlay.status` crudo a `StatusBadge`, así que un SGP cuyo partido ya empezó
// seguía mostrando literalmente "VALIDO" (el fallback de `StatusBadge` para cualquier
// string sin traducción) hasta que la liquidación real corriera, a veces horas después del
// pitido inicial -- bug real reportado en producción. Nunca borra/reescribe el `status`
// real (la liquidación sigue siendo la única fuente de verdad de ganada/perdida/void) --
// solo decide qué ETIQUETA mostrar en este instante.
export function parlayDisplayStatus(status: string | null | undefined, isBettable: boolean | undefined): string | null {
  if (!status) return null
  if (status === 'valido' && isBettable === false) return 'en_juego'
  // Home Premium + Lifecycle (01/10/2026, Sección 6 del encargo): un parlay `valido` cuyos
  // fixtures TODAVÍA no empezaron (is_bettable !== false) caía al fallback genérico de
  // `StatusBadge` (mostraba literalmente "VALIDO") -- el lifecycle pedido es PENDIENTE → EN
  // JUEGO → ACERTÓ/FALLÓ/VOID, nunca un estado interno crudo sin traducir.
  if (status === 'valido') return 'pendiente'
  return status
}

export function riskTone(level: string | null | undefined): BadgeTone {
  const normalized = (level ?? '').toLowerCase()
  if (normalized.includes('alto')) return 'danger'
  if (normalized.includes('medio')) return 'warn'
  if (normalized.includes('bajo')) return 'success'
  return 'neutral'
}

export function confidenceTone(score: number | null | undefined): BadgeTone {
  if (score == null) return 'neutral'
  if (score >= 70) return 'success'
  if (score >= 45) return 'warn'
  return 'danger'
}

export function confidenceLabel(score: number | null | undefined): string {
  if (score == null) return '—'
  if (score >= 70) return 'Alta'
  if (score >= 45) return 'Media'
  return 'Baja'
}
